import XLSX from "xlsx-js-style";
import moment from "moment";
import { formatStat } from "../../../helper/statsFormatter";
import { toast } from "../../../common_components/Toast/ToastUtils";
const titleStyle = {
    font: { bold: true, sz: 16, color: { rgb: "111827" } },
    alignment: { horizontal: "center", vertical: "center" },
};
const infoStyle = {
    font: { sz: 11, color: { rgb: "4B5563" } },
    alignment: { horizontal: "center", vertical: "center" },
};
const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1F2937" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "374151" } },
        bottom: { style: "thin", color: { rgb: "374151" } },
        left: { style: "thin", color: { rgb: "374151" } },
        right: { style: "thin", color: { rgb: "374151" } },
    },
};
const sectionHeaderStyle = {
    font: { bold: true, sz: 12, color: { rgb: "111827" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
    },
};
const cellStyle = {
    font: { sz: 10, color: { rgb: "1F2937" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "F3F4F6" } },
        bottom: { style: "thin", color: { rgb: "F3F4F6" } },
        left: { style: "thin", color: { rgb: "F3F4F6" } },
        right: { style: "thin", color: { rgb: "F3F4F6" } },
    },
};
const summaryRowStyle = {
    font: { bold: true, sz: 10, color: { rgb: "111827" } },
    fill: { fgColor: { rgb: "F9FAFB" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "medium", color: { rgb: "9CA3AF" } },
    },
};
const formatValue = (val, key) => {
    return formatStat(val ?? 0, key);
};
export const exportPerformanceReport = (data, options) => {
    if (!data || !data.charts) {
        toast.error("No data available to export.");
        return;
    }
    try {
        const { charts, overall_stats } = data;
        const allRows = [];
        let maxCols = 6;
        // 1. Title & Header Info
        allRows.push([{ v: "Performance Report", t: "s", s: titleStyle }]);
        allRows.push([{ v: `Client: ${options.clientName}`, t: "s", s: infoStyle }]);
        allRows.push([{
                v: `Date Range: ${options.startDate} to ${options.endDate}${options.compareStartDate ? ` (vs ${options.compareStartDate} to ${options.compareEndDate})` : ""}`,
                t: "s",
                s: infoStyle
            }]);
        allRows.push([]); // Spacer
        allRows.push([]); // Spacer
        // 3. Helper to append a tab's data as a section
        const appendTabSection = (tabKey, tabName) => {
            const chartData = charts[tabKey];
            if (!chartData || !chartData.labels || chartData.labels.length === 0)
                return;
            const labels = chartData.labels;
            const series = chartData.series;
            const metricKeys = Object.keys(series);
            allRows.push([{ v: `${tabName.toUpperCase()} DATA`, t: "s", s: sectionHeaderStyle }]);
            // Header Row
            const headers = ["Date", ...metricKeys.map(k => k.replace(/_/g, " ").toUpperCase())];
            maxCols = Math.max(maxCols, headers.length);
            allRows.push(headers);
            // Data Rows
            labels.forEach((label, index) => {
                const row = [label];
                metricKeys.forEach(key => {
                    row.push(formatValue(series[key][index], key));
                });
                allRows.push(row);
            });
            allRows.push([]); // Spacer
        };
        // Append each section
        appendTabSection('all', 'All Channels');
        appendTabSection('meta', 'Meta Ads');
        appendTabSection('adword', 'Google Ads');
        // 4. Create Workbook & Apply Styling
        const worksheet = XLSX.utils.aoa_to_sheet(allRows);
        // Basic Merges for Title/Info
        worksheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: maxCols - 1 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: maxCols - 1 } },
        ];
        // Apply styles dynamically based on row content
        allRows.forEach((row, rowIndex) => {
            const firstCell = row[0];
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
            // Styling Title and Section Headers
            if (rowIndex < 3) {
                // Already handled or applied via AOE if we use cell objects
            }
            // Iterate columns to apply cell styles
            row.forEach((_, colIndex) => {
                const currentRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                if (!worksheet[currentRef])
                    return;
                // Title Style
                if (rowIndex === 0)
                    worksheet[currentRef].s = titleStyle;
                else if (rowIndex < 3)
                    worksheet[currentRef].s = infoStyle;
                // Section Headers
                if (typeof firstCell === 'object' && firstCell?.s === sectionHeaderStyle) {
                    worksheet[currentRef].s = sectionHeaderStyle;
                    // Merge section header across width
                    if (colIndex === 0) {
                        worksheet["!merges"]?.push({ s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: maxCols - 1 } });
                    }
                }
                // Table Headers (if first cell is "Date" or "Metric")
                if (firstCell === "Date" || firstCell === "Metric") {
                    worksheet[currentRef].s = headerStyle;
                }
                // Summary Stats Mini Headers
                if (firstCell === "SUMMARY STATS" || (typeof firstCell === 'string' && firstCell.includes("SUMMARY"))) {
                    worksheet[currentRef].s = sectionHeaderStyle;
                }
                // Default Cell Style for data rows
                if (!worksheet[currentRef].s) {
                    worksheet[currentRef].s = cellStyle;
                }
            });
        });
        // Column Widths
        const colWidths = [{ wch: 18 }];
        for (let i = 1; i < maxCols; i++)
            colWidths.push({ wch: 18 });
        worksheet["!cols"] = colWidths;
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Performance Report");
        const fileName = `Performance_Report_${options.clientName.replace(/\s+/g, "_")}_${moment().format("YYYYMMDD")}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    }
    catch (error) {
        console.error("Export failed", error);
        toast.error("Export failed. Please try again.");
    }
};
export default exportPerformanceReport;
